import {
  FoldVertical,
  Maximize2,
  Minus,
  Plus,
  Printer,
  Search,
  UnfoldVertical,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PersonAvatar } from "@/components/persons/PersonAvatar";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { OriginLink } from "@/components/shared/origin-link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useOrgChart } from "@/hooks/use-org-chart";
import { useMyPerson } from "@/hooks/use-persons";
import {
  buildOrgForest,
  chainOfCommandIds,
  type OrgChartNode,
  type OrgChartPerson,
} from "@/lib/org-chart";
import { cn } from "@/lib/utils";

// Zoom is a CSS transform scale on the tree content; these bound it to a
// range that stays legible (0.4) without over-magnifying (1.5).
const ZOOM_MIN = 0.4;
const ZOOM_MAX = 1.5;
const ZOOM_STEP = 0.1;
const clampZoom = (z: number): number =>
  Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100));

interface ChartState {
  myPersonId: string | null;
  highlightedId: string | null;
  isExpanded: (id: string) => boolean;
  toggle: (id: string) => void;
}

// Brand scale, one per top-level branch: every card in an executive's
// subtree carries the same left-edge accent so departments read at a
// glance. The root (CEO) card falls back to primary navy.
const BRANCH_ACCENTS = [
  "border-l-chart-1",
  "border-l-chart-2",
  "border-l-chart-3",
  "border-l-chart-4",
  "border-l-chart-5",
];

function NodeCard({
  node,
  state,
  accent,
}: {
  node: OrgChartNode;
  state: ChartState;
  accent?: string;
}) {
  const { person, children } = node;
  const isMe = person.id === state.myPersonId;
  const isHighlighted = person.id === state.highlightedId;
  const expanded = state.isExpanded(person.id);

  return (
    <div
      data-person-id={person.id}
      className={cn(
        "w-44 rounded-lg border border-l-4 bg-card px-3 py-2 text-left shadow-xs",
        accent ?? "border-l-primary",
        isMe && "ring-2 ring-primary",
        // Brief locate flash after Find person; ring-offset lifts it clear of
        // the card so it reads even on top of the me-ring.
        isHighlighted && "ring-2 ring-primary ring-offset-2 ring-offset-background animate-pulse",
      )}
    >
      <div className="flex items-center gap-2">
        <PersonAvatar name={person.name} seed={person.id} size="sm" imageUrl={person.avatarUrl} />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <OriginLink
              to="/dashboard/persons/$personId"
              params={{ personId: person.id }}
              className="truncate text-sm font-medium hover:text-primary hover:underline"
            >
              {person.name}
            </OriginLink>
            {isMe && (
              <span className="shrink-0 rounded-full bg-primary px-1.5 text-3xs font-medium text-primary-foreground">
                You
              </span>
            )}
          </div>
          {person.title && (
            <div className="truncate text-xs text-muted-foreground">{person.title}</div>
          )}
        </div>
      </div>
      {children.length > 0 && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => state.toggle(person.id)}
          className="mt-1.5 flex w-full items-center justify-center gap-1 rounded-md border border-dashed py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          {expanded ? <Minus className="size-3" /> : <Plus className="size-3" />}
          {children.length} {children.length === 1 ? "report" : "reports"}
        </button>
      )}
    </div>
  );
}

/**
 * Two or more childless reports render as one compact vertical column
 * instead of spreading horizontally — the main defense against runaway
 * chart width. A spine runs down the left edge with a stub into each card;
 * the jog at the top connects the spine back to the parent's drop line at
 * the column's center.
 */
function LeafStack({
  nodes,
  state,
  accentOf,
}: {
  nodes: OrgChartNode[];
  state: ChartState;
  accentOf: (node: OrgChartNode) => string | undefined;
}) {
  return (
    <div className="relative">
      <div className="absolute left-0 right-1/2 top-0 h-px bg-border" />
      {nodes.map((node, i) => (
        <div key={node.person.id} className="relative pl-3 pt-2">
          {/* spine segment; the last card's stops at its own stub */}
          <div
            className={cn(
              "absolute left-0 top-0 w-px bg-border",
              i === nodes.length - 1 ? "h-[calc(50%+0.25rem)]" : "h-full",
            )}
          />
          <div className="relative">
            <div className="absolute -left-3 top-1/2 h-px w-3 bg-border" />
            <NodeCard node={node} state={state} accent={accentOf(node)} />
          </div>
        </div>
      ))}
    </div>
  );
}

function TreeNode({
  node,
  state,
  accent,
}: {
  node: OrgChartNode;
  state: ChartState;
  accent?: string;
}) {
  const { children } = node;
  const expanded = state.isExpanded(node.person.id) && children.length > 0;

  // A node with no accent of its own (the root) deals each child a branch
  // color; everyone below inherits their branch's color unchanged.
  const childAccents = new Map<string, string | undefined>(
    children.map((c, i) => [c.person.id, accent ?? BRANCH_ACCENTS[i % BRANCH_ACCENTS.length]]),
  );

  // Reports who manage teams of their own get a horizontal slot each;
  // childless reports collapse into a single LeafStack column at the end.
  const branches = children.filter((c) => c.children.length > 0);
  const leaves = children.filter((c) => c.children.length === 0);
  const slots: Array<{ key: string; content: ReactNode }> = branches.map((child) => ({
    key: child.person.id,
    content: <TreeNode node={child} state={state} accent={childAccents.get(child.person.id)} />,
  }));
  if (leaves.length >= 2) {
    slots.push({
      key: "leaf-stack",
      content: (
        <LeafStack nodes={leaves} state={state} accentOf={(n) => childAccents.get(n.person.id)} />
      ),
    });
  } else {
    for (const leaf of leaves) {
      slots.push({
        key: leaf.person.id,
        content: <TreeNode node={leaf} state={state} accent={childAccents.get(leaf.person.id)} />,
      });
    }
  }

  return (
    <div className="flex flex-col items-center">
      <NodeCard node={node} state={state} accent={accent} />
      {expanded && (
        <>
          <div className="h-5 w-px bg-border" />
          <div className="flex items-start">
            {slots.map((slot, i) => (
              <div key={slot.key} className="relative px-2 pt-5">
                {/* vertical stub from the sibling rail down to this slot */}
                <div className="absolute left-1/2 top-0 h-5 w-px -translate-x-1/2 bg-border" />
                {/* horizontal sibling rail; outer slots only draw their inner half */}
                {slots.length > 1 && (
                  <div
                    className={cn(
                      "absolute top-0 h-px bg-border",
                      i === 0
                        ? "left-1/2 right-0"
                        : i === slots.length - 1
                          ? "left-0 right-1/2"
                          : "left-0 right-0",
                    )}
                  />
                )}
                {slot.content}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function collectExpandableIds(nodes: OrgChartNode[], into: string[] = []): string[] {
  for (const n of nodes) {
    if (n.children.length > 0) {
      into.push(n.person.id);
      collectExpandableIds(n.children, into);
    }
  }
  return into;
}

const MAX_MATCHES = 8;

/**
 * Type-to-locate box. Matches active people by name (case-insensitive
 * substring); selecting one (click, or Enter on the active row) asks the
 * chart to expand the person's ancestors, scroll their card into view, and
 * flash it. Purely presentational — the reveal work lives in the parent.
 */
function PersonSearch({
  people,
  onLocate,
}: {
  people: OrgChartPerson[];
  onLocate: (id: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return people.filter((p) => p.name.toLowerCase().includes(q)).slice(0, MAX_MATCHES);
  }, [people, query]);

  const select = (person: OrgChartPerson) => {
    onLocate(person.id);
    setQuery("");
    setOpen(false);
    setActiveIndex(0);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (matches.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % matches.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + matches.length) % matches.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const match = matches[activeIndex] ?? matches[0];
      if (match) select(match);
    }
  };

  return (
    <div
      className="relative w-full max-w-xs"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="search"
        aria-label="Find a person in the org chart"
        placeholder="Find a person…"
        className="h-8 pl-8"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActiveIndex(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && matches.length > 0 && (
        <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
          {matches.map((person, i) => (
            <li key={person.id}>
              <button
                type="button"
                aria-current={i === activeIndex}
                className={cn(
                  "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm",
                  i === activeIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent/60",
                )}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => select(person)}
              >
                <PersonAvatar
                  name={person.name}
                  seed={person.id}
                  size="sm"
                  imageUrl={person.avatarUrl}
                />
                <span className="min-w-0">
                  <span className="block truncate font-medium">{person.name}</span>
                  {person.title && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {person.title}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Pyramid-style people org chart built from `person.managerId`. On load, the
 * signed-in user's chain of command is expanded (their card highlighted);
 * every other branch starts collapsed and can be opened node by node.
 */
export function OrgChart() {
  const { data: people, isLoading, isError, refetch } = useOrgChart();
  // Best-effort: an unlinked account (or a failed lookup) just means no
  // highlighted card and a top-level default view — never an error state.
  const { data: myPerson } = useMyPerson();

  // Per-node open/closed choices layered over the computed default, so the
  // default can depend on async data without state-syncing effects.
  const [overrides, setOverrides] = useState<Map<string, boolean>>(new Map());

  // Once the chart and the signed-in user's card are both on screen, scroll
  // the card to the horizontal center — once per person, so background
  // refetches don't yank the user's scroll position back.
  const scrollRef = useRef<HTMLDivElement>(null);
  const centeredFor = useRef<string | null>(null);
  const myId = myPerson?.id ?? null;
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !myId || !people || centeredFor.current === myId) return;
    const card = container.querySelector(`[data-person-id="${CSS.escape(myId)}"]`);
    if (!(card instanceof HTMLElement)) return;
    const containerRect = container.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    container.scrollLeft +=
      cardRect.left - containerRect.left - (containerRect.width - cardRect.width) / 2;
    centeredFor.current = myId;
  }, [myId, people]);

  // Expanding a node re-centers every ancestor over its wider subtree,
  // which used to shift the whole chart under the cursor. Anchor instead:
  // remember where the toggled card sat on screen, then compensate
  // scrollLeft right after the re-render (before paint) so it stays put.
  const pendingAnchor = useRef<{ id: string; left: number } | null>(null);
  useLayoutEffect(() => {
    const anchor = pendingAnchor.current;
    if (!anchor) return;
    pendingAnchor.current = null;
    const container = scrollRef.current;
    const card = container?.querySelector(`[data-person-id="${CSS.escape(anchor.id)}"]`);
    if (!container || !(card instanceof HTMLElement)) return;
    container.scrollLeft += card.getBoundingClientRect().left - anchor.left;
  });

  // CSS transform scale on the tree content. transform-origin is top-left so
  // scrollLeft/scrollTop stay 1:1 with on-screen pixels — the auto-center and
  // anchor math above measure visual rects and keep working at any zoom.
  const innerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);

  // A CSS transform scales the tree visually but leaves its layout box at the
  // UNSCALED size, so the scroll container's scrollWidth/scrollHeight ignore the
  // zoom: zooming out stranded a large empty scroll region past the tree (and
  // zooming in clipped the far edges). Compensate the transformed element with a
  // right/bottom margin equal to the size delta (negative when zoomed out,
  // positive when zoomed in) so the scrollable area matches what's on screen.
  // Margins don't shift the top-left origin, so the center/anchor math is
  // unaffected. Re-measured on zoom change and whenever the tree resizes
  // (expand/collapse) via a ResizeObserver on the unscaled layout box.
  const contentRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const write = () => {
      const mr = `${el.offsetWidth * (zoom - 1)}px`;
      const mb = `${el.offsetHeight * (zoom - 1)}px`;
      // Skip no-op writes so nothing dirties layout unnecessarily.
      if (el.style.marginRight !== mr) el.style.marginRight = mr;
      if (el.style.marginBottom !== mb) el.style.marginBottom = mb;
    };
    write();
    if (typeof ResizeObserver === "undefined") return;
    // Defer the write out of the observation callback: setting a margin can
    // toggle a scrollbar (notably as the scaled width crosses the viewport
    // near ~70%), which the browser flags as "ResizeObserver loop completed
    // with undelivered notifications" if mutated synchronously. A rAF hop
    // moves it to the next frame, breaking the delivery loop.
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(write);
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [zoom]);

  // Ctrl/⌘ + wheel zooms; a plain wheel keeps scrolling/panning. Attached to
  // the node via a callback ref (not React's passive onWheel) so
  // preventDefault suppresses the browser's own page zoom — and so it (re)binds
  // correctly when the scroll container mounts after the loading state.
  const wheelCleanup = useRef<(() => void) | null>(null);
  const setScrollRef = useCallback((node: HTMLDivElement | null) => {
    wheelCleanup.current?.();
    wheelCleanup.current = null;
    scrollRef.current = node;
    if (!node) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setZoom((z) => clampZoom(z + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP)));
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    wheelCleanup.current = () => node.removeEventListener("wheel", onWheel);
  }, []);

  // Find person: a selection expands the target's ancestors (below) and then
  // asks this effect to scroll the card to the center of the viewport — both
  // axes, since the tree grows down as well as across — and flash it.
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [pendingFocus, setPendingFocus] = useState<{ id: string; nonce: number } | null>(null);
  useEffect(() => {
    if (!pendingFocus) return;
    const container = scrollRef.current;
    if (!container) return;
    const raf = requestAnimationFrame(() => {
      const card = container.querySelector(`[data-person-id="${CSS.escape(pendingFocus.id)}"]`);
      if (!(card instanceof HTMLElement)) return;
      const c = container.getBoundingClientRect();
      const k = card.getBoundingClientRect();
      container.scrollLeft += k.left - c.left - (c.width - k.width) / 2;
      container.scrollTop += k.top - c.top - (c.height - k.height) / 2;
      setHighlightedId(pendingFocus.id);
    });
    return () => cancelAnimationFrame(raf);
  }, [pendingFocus]);
  useEffect(() => {
    if (!highlightedId) return;
    const t = setTimeout(() => setHighlightedId(null), 2000);
    return () => clearTimeout(t);
  }, [highlightedId]);

  const forest = useMemo(() => buildOrgForest(people ?? []), [people]);
  const defaultExpanded = useMemo(() => {
    if (!people) return new Set<string>();
    const mine = myPerson ? chainOfCommandIds(people, myPerson.id) : new Set<string>();
    // Not in the chart (unlinked or inactive): show the top level instead.
    return mine.size > 0 ? mine : new Set(forest.map((n) => n.person.id));
  }, [people, myPerson, forest]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (isError) {
    return <ErrorState message="Failed to load the org chart" onRetry={() => refetch()} />;
  }
  if (!people || people.length === 0) {
    return <EmptyState title="No people yet" description="Add people to see the org chart." />;
  }

  const state: ChartState = {
    myPersonId: myPerson?.id ?? null,
    highlightedId,
    isExpanded: (id) => overrides.get(id) ?? defaultExpanded.has(id),
    toggle: (id) => {
      const card = scrollRef.current?.querySelector(`[data-person-id="${CSS.escape(id)}"]`);
      if (card instanceof HTMLElement) {
        pendingAnchor.current = { id, left: card.getBoundingClientRect().left };
      }
      setOverrides((prev) => {
        const next = new Map(prev);
        next.set(id, !(prev.get(id) ?? defaultExpanded.has(id)));
        return next;
      });
    },
  };

  const setAll = (open: boolean) =>
    setOverrides(new Map(collectExpandableIds(forest).map((id) => [id, open])));

  // Reveal a searched-for person: expand every ancestor (the chain includes
  // the person themself, whose reports then show), then hand off to the focus
  // effect to scroll + flash once the re-render settles.
  const locatePerson = (id: string) => {
    const chain = chainOfCommandIds(people, id);
    setOverrides((prev) => {
      const next = new Map(prev);
      for (const cid of chain) next.set(cid, true);
      return next;
    });
    setPendingFocus({ id, nonce: Date.now() });
  };

  // Fit: scale so the whole (unscaled) tree fits the viewport, capped at 100%
  // so small charts don't magnify. Measures the inner content box, not the
  // stretch-to-fill flex row, so a chart that already fits reads as 100%.
  const fitToView = () => {
    const container = scrollRef.current;
    const inner = innerRef.current;
    if (!container || !inner) return;
    const pad = 48; // p-6 → 1.5rem of padding on each side
    const w = inner.scrollWidth;
    const h = inner.scrollHeight;
    if (w <= 0 || h <= 0) return;
    setZoom(
      clampZoom(Math.min((container.clientWidth - pad) / w, (container.clientHeight - pad) / h, 1)),
    );
    requestAnimationFrame(() => {
      const c = scrollRef.current;
      if (!c) return;
      c.scrollLeft = 0;
      c.scrollTop = 0;
    });
  };

  // Print: expand everything and drop to 100% so nothing is clipped or hidden,
  // then let React flush before the print dialog snapshots the DOM. The print
  // stylesheet (globals.css) hides app chrome + these controls.
  const handlePrint = () => {
    setAll(true);
    setZoom(1);
    window.setTimeout(() => window.print(), 250);
  };

  return (
    <div className="org-chart-print-area flex h-full min-h-0 flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2" data-print-hide>
        <PersonSearch people={people} onLocate={locatePerson} />
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setAll(true)}>
            <UnfoldVertical className="size-3.5" />
            Expand all
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAll(false)}>
            <FoldVertical className="size-3.5" />
            Collapse all
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Printer className="size-3.5" />
            Print
          </Button>
        </div>
      </div>
      <div className="relative min-h-0 flex-1">
        <div
          ref={setScrollRef}
          className="org-chart-scroll h-full overflow-auto rounded-lg border bg-muted/20 p-6"
        >
          <div
            ref={contentRef}
            className="org-chart-content flex min-w-max origin-top-left items-start justify-center gap-6"
            style={{ transform: `scale(${zoom})` }}
          >
            <div ref={innerRef} className="flex items-start gap-6">
              {forest.map((root) => (
                <TreeNode key={root.person.id} node={root} state={state} />
              ))}
            </div>
          </div>
        </div>
        {/* Zoom / fit cluster — pinned to the viewport box, not the scrolled tree. */}
        <div
          className="absolute bottom-3 right-3 flex items-center gap-1 rounded-md border bg-background/95 p-1 shadow-md backdrop-blur"
          data-print-hide
        >
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Zoom out"
            disabled={zoom <= ZOOM_MIN}
            onClick={() => setZoom((z) => clampZoom(z - ZOOM_STEP))}
          >
            <ZoomOut className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-14 tabular-nums"
            aria-label="Reset zoom to 100%"
            onClick={() => setZoom(1)}
          >
            {Math.round(zoom * 100)}%
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Zoom in"
            disabled={zoom >= ZOOM_MAX}
            onClick={() => setZoom((z) => clampZoom(z + ZOOM_STEP))}
          >
            <ZoomIn className="size-4" />
          </Button>
          <Button variant="ghost" size="icon-sm" aria-label="Fit chart to view" onClick={fitToView}>
            <Maximize2 className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
